// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react'
import {
  DesignVersionProvider,
  DialogUnwrapped,
  ButtonPrimary,
  ParticleDesignVersions
} from '@klarna/bubble-ui'
import { setupSentry, reportError } from '../../sentry'
import LearnMoreContent from './LearnMoreDefault/LearnMoreDefault'
import ErrorBoundary from '../ErrorBoundary'

import Bespoke from './bespoke'
import { getCopyParams, getLocale } from './utils'

import initTranslation from './utils/initTranslation'
import sendInterstitialEvent from '../../../tracking/sendInterstitialEvent/sendInterstitialEvent'
import { TokenParams } from './utils/createToken/createToken'
import styles from '../../widgets/css/index.css'
import backgroundImg from '../../widgets/css/background.jpg'
import GkdAuthParams from '../../../types/GkdAuthParams'

import NewInterstitials from '../../../newInterstitials'
import { startPrequalification } from '../../../utils/prequalification/startPrequalification'
import { SkeletonLoader } from '../../../commons/skeletonLoader'
import { useMobileSDKContext } from '../../../services/MobileSDKMessaging/MobileSDKMessagingContext'

interface LearnMoreComponentProps {
  params: TokenParams
  onClose?: () => void
  showBackground: boolean
  focusTrapActive?: boolean
  isWebSDK?: boolean
  clientKey?: string
  gkdAuthParams: GkdAuthParams
}

export default function LearnMoreComponent({
  params,
  onClose,
  showBackground,
  focusTrapActive = true,
  isWebSDK = false,
  gkdAuthParams = {}
}: LearnMoreComponentProps) {
  const legacyOSMClientId = params.legacyOSMClientId || params.clientId
  const { interstitialTriggerer } = params
  const transformedParams = {
    ...params, legacyOSMClientId, interstitialTriggerer, focusTrapActive, showButtons: onClose ?? false
  }

  if (params?.openPreQualFlow) {
    startPrequalification(transformedParams, params.language, params.country)
    return (
      <SkeletonLoader/>
    )
  }

  delete transformedParams.clientId // removing this key to clear confusion between GKD Auth clientId and OSM clientId

  const designVersion = transformedParams.theme === 'dark'
    ? ParticleDesignVersions.WAFFLE_DARK
    : ParticleDesignVersions.WAFFLE_LIGHT

  const rootElement = document.getElementById('root')
  if (rootElement) {
    rootElement.ariaModal = true
    rootElement.role = 'dialog'
    rootElement.setAttribute('aria-labelledby', 'klarna-dialog-learn-more')
    rootElement.tabIndex = -1
  }
  const { messagingSDK } = useMobileSDKContext()

  const handleCloseInterstitial = useCallback(async () => {
    // send signals to close fullscreen view if in mobile SDK context
    if (messagingSDK) {
      try {
        await messagingSDK.hideSeparateFullscreen()
      } catch (err) {
        // silent error (fallback mechanism)
      }
    }

    if (onClose) {
      onClose()
    }
  }, [onClose, messagingSDK])

  if (
    [
      'payment-information-generic-interstitial',
      'payment-calculator-interstitial',
      'payment-calculator-interstitial-revamped',
      'simple-timeline-based-interstitial',
      'deals-interstitial'
    ].includes(transformedParams.bespokeId) && !params.openPreQualFlow
  ) {
    const props = { ...transformedParams, ...gkdAuthParams }
    return (
      <NewInterstitials {...props} onClose={handleCloseInterstitial} role='dialog' />
    )
  }

  const [{ translator, copy, error }, setTranslation] = useState<any>({})

  setupSentry()

  useEffect(() => {
    sendInterstitialEvent(transformedParams.locale, 'interstitial_view', {
      ...transformedParams,
      type: 'learn-more',
      bespokeId:
        transformedParams.bespokeId && Bespoke[transformedParams.bespokeId] ? transformedParams.bespokeId : '',
      translation: getLocale(transformedParams),
      isWebSDK,
      k: params?.trackerParameters?.k,
    })

    initTranslation(transformedParams, gkdAuthParams).then((translation: any) => {
      setTranslation(translation)
    })
  }, [params]) // intentionally set to this instead of transformedParams, useEffect will render multiple times if we use transformedParams

  if (!translator) return null

  const footerButtons = []
  const adType = getCopyParams(transformedParams).copyParams[0]

  if (onClose) {
    const closeButton = {
      Component: ButtonPrimary,
      props: {
        children: translator.translate('buttons.close'),
        loading: false,
        id: 'learn-more-close-button',
        onClick: handleCloseInterstitial,
      },
    }
    footerButtons.push(closeButton)
  }

  if (error) {
    sendInterstitialEvent(transformedParams.locale, 'interstitial_error', {
      error,
      isWebSDK,
      ...params,
      k: params?.trackerParameters?.k,
    })
    reportError(
      {
        ...transformedParams,
        copy: copy ? JSON.stringify(copy) : null,
      },
      error
    )
  }

  let Component = () => <LearnMoreContent copy={copy} params={transformedParams} />

  const backgroundTheme = transformedParams.theme !== 'dark'
    && (transformedParams.bespokeId === 'external_content'
      || transformedParams.bespokeId === 'checkout')
    ? 'colors/backgrounds/subtle-inline'
    : 'colors/backgrounds/default'

  if (transformedParams.bespokeId && Bespoke[transformedParams.bespokeId]) {
    Component = Bespoke[transformedParams.bespokeId]
  }

  const onError = (err: any) => {
    sendInterstitialEvent(transformedParams.locale, 'interstitial_error', {
      error: err,
      isWebSDK,
      ...params,
      k: params?.trackerParameters?.k,
    })
    reportError(
      {
        ...transformedParams,
        copy: copy ? JSON.stringify(copy) : null,
      },
      err
    )
  }

  return (

    <div
      role='dialog'
      aria-labelledby='klarna-dialog-learn-more'
      id={showBackground ? 'widget-with-background' : undefined}
      style={
        showBackground
          ? {
            backgroundImage: `url(${backgroundImg})`,
          }
          : null
      }
      className={isWebSDK ? 'websdk' : undefined}
    >
      <style>{styles}</style>
      <ErrorBoundary
        onClose={handleCloseInterstitial}
        translator={translator}
        onError={(err: any) => onError(err)}
      >
        <DesignVersionProvider value={designVersion}>
          <DialogUnwrapped
            focusTrapActive={focusTrapActive}
            onClose={onClose ? handleCloseInterstitial : null}
            captureEscapeKey={true}
            rightIconAriaLabel={translator.translate('buttons.close')}
            onBackgroundClick={handleCloseInterstitial}
            klarnaLogo
            klarnaLogoAccessibilityLabel="Klarna logo"
            footer={footerButtons}
            backgroundColorToken={backgroundTheme}
            id={`learn-more-dialog-${transformedParams.bespokeId || 'default'}-${
              transformedParams.theme || 'default'
            }`}
          >
            <div id="interstitial-wrapper">
              <Component
                designVersion={designVersion}
                copy={copy}
                closeButtonLabel={translator.translate('buttons.close')}
                buttons={footerButtons}
                onClose={handleCloseInterstitial}
                theme={transformedParams.theme}
                adType={adType}
              />
            </div>
          </DialogUnwrapped>
        </DesignVersionProvider>
      </ErrorBoundary>
    </div>
  )
}
